"use client"

import HeaderSearch from "@/modules/layout/components/header-search"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"
import { usePathname } from "next/navigation"
import { Suspense, useEffect, useState } from "react"
import { createPortal } from "react-dom"

type Cat = HttpTypes.StoreProductCategory

const MobileMenu = ({ categories, approved = false }: { categories: Cat[]; approved?: boolean }) => {
  const [open, setOpen] = useState(false)
  const [section, setSection] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const pathname = usePathname()

  useEffect(() => setMounted(true), [])
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : ""
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false)
    document.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const top = categories.filter((c) => !c.parent_category_id).sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
  const kids = (id: string) => categories.filter((c) => c.parent_category_id === id).sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))

  return (
    <>
      <button
        className="small:hidden h-10 w-10 -ml-2 flex items-center justify-center"
        aria-label="Open menu"
        aria-expanded={open}
        aria-controls="mobile-menu"
        onClick={() => setOpen(true)}
      >
        <svg aria-hidden width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>
      {open &&
        mounted &&
        createPortal(
          <div className="fixed inset-0 z-[100] small:hidden" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <nav id="mobile-menu" className="absolute left-0 top-0 bottom-0 w-[88%] max-w-sm bg-white flex flex-col overflow-y-auto">
              <div className="flex items-center justify-between px-4 h-16 border-b border-elevate-border">
                <img src="/elevate-logo.png" alt="Elevate Wholesale" width={616} height={200} className="h-9 w-auto" />
                <button aria-label="Close menu" className="h-10 w-10 flex items-center justify-center" onClick={() => setOpen(false)}>
                  <svg aria-hidden width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
              <div className="p-4 border-b border-elevate-border">
                <Suspense>
                  <HeaderSearch onSubmitted={() => setOpen(false)} />
                </Suspense>
              </div>
              <ul className="flex flex-col">
                {top.map((cat) => {
                  const isOpen = section === cat.id
                  return (
                    <li key={cat.id} className="border-b border-elevate-border">
                      <button
                        className="w-full flex items-center justify-between px-4 py-4 font-heading text-lg"
                        aria-expanded={isOpen}
                        onClick={() => setSection(isOpen ? null : cat.id)}
                      >
                        {cat.name}
                        <span aria-hidden className={clx("transition-transform text-elevate-grey-mid", isOpen && "rotate-45")}>+</span>
                      </button>
                      {isOpen && (
                        <ul className="pb-3">
                          {kids(cat.id).map((c) => (
                            <li key={c.id}>
                              <LocalizedClientLink href={`/categories/${c.handle}`} className="flex justify-between px-6 py-2.5 text-sm hover:bg-elevate-cream">
                                <span>{c.name}</span>
                                <span className="text-elevate-grey-mid text-xs">{(c as any).product_count ?? c.products?.length ?? 0}</span>
                              </LocalizedClientLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
                {[
                  ["All products", "/store"],
                  approved ? ["Quick order", "/quick-order"] : ["Trade signup", "/account?view=register"],
                  ...(approved ? [["Specials", "/specials"]] : []),
                  ["My account", "/account"],
                  ["Partner with us", "/brand-partnerships"],
                ].map(([label, href]) => (
                  <li key={label} className="border-b border-elevate-border">
                    <LocalizedClientLink href={href} className="block px-4 py-4 font-heading text-lg">
                      {label}
                    </LocalizedClientLink>
                  </li>
                ))}
              </ul>
              <div className="mt-auto p-4 bg-elevate-cream text-sm flex flex-col gap-1">
                <span className="text-xs uppercase tracking-[0.15em] text-elevate-gold-dark">Talk to us</span>
                <a href="tel:+61370733813" className="font-heading text-xl">+61 3 7073 3813</a>
                <a href="mailto:sales@elevatewholesale.com.au" className="underline underline-offset-2">sales@elevatewholesale.com.au</a>
              </div>
            </nav>
          </div>,
          document.body
        )}
    </>
  )
}

export default MobileMenu
